// Chain finality configuration based on Circle's CCTP documentation
// https://developers.circle.com/cctp/required-block-confirmations

import { SupportedChainId } from './chains';

// Estimated finality times in minutes based on Circle's documentation
export const CHAIN_FINALITY_TIMES = {
  [SupportedChainId.ETHEREUM]: {
    min: 13,
    max: 19,
    typical: 16
  },
  [SupportedChainId.AVALANCHE]: {
    min: 0.13,  // ~8 seconds
    max: 0.13,
    typical: 0.13
  },
  [SupportedChainId.OPTIMISM]: {
    min: 13,  // ~65 ETH blocks
    max: 19,
    typical: 16
  },
  [SupportedChainId.ARBITRUM]: {
    min: 13,  // ~65 ETH blocks
    max: 19,
    typical: 16
  },
  [SupportedChainId.BASE]: {
    min: 13,  // ~65 ETH blocks
    max: 19,
    typical: 16
  },
  [SupportedChainId.POLYGON]: {
    min: 0.13,  // ~8 seconds (2-3 blocks)
    max: 0.13,
    typical: 0.13
  },
  [SupportedChainId.UNICHAIN]: {
    min: 13,  // Similar to Ethereum L2s
    max: 19,
    typical: 16
  },
  [SupportedChainId.LINEA]: {
    min: 360,  // 6 hours in minutes
    max: 1920, // 32 hours in minutes
    typical: 840 // ~14 hours (average)
  },
  [SupportedChainId.SONIC]: {
    min: 0.13,  // ~8 seconds (already fast like Avalanche/Polygon)
    max: 0.13,
    typical: 0.13
  }
};

// Get estimated time for finality in minutes
export const getEstimatedFinalityTime = (chainId, mode = 'typical') => {
  // Fast transfers always take ~8 seconds regardless of chain
  if (mode === 'fast') {
    return 0.13; // ~8 seconds
  }
  
  const times = CHAIN_FINALITY_TIMES[chainId];
  if (!times) return 16; // Default to Ethereum-like timing
  
  if (mode === 'min') return times.min;
  if (mode === 'max') return times.max;
  return times.typical;
};

// Get formatted time string
export const getFormattedFinalityTime = (chainId, mode = 'typical') => {
  // Fast transfers always take ~8 seconds
  if (mode === 'fast') {
    return '~8 seconds';
  }
  
  const times = CHAIN_FINALITY_TIMES[chainId];
  if (!times) return '~15-20 minutes';
  
  const minMinutes = times.min;
  const maxMinutes = times.max;
  
  // For very fast chains (under 1 minute)
  if (maxMinutes < 1) {
    const seconds = Math.round(maxMinutes * 60);
    return `~${seconds} seconds`;
  }
  
  // For chains with a range
  if (minMinutes !== maxMinutes) {
    // If it's more than 60 minutes, display in hours
    if (minMinutes >= 60 || maxMinutes >= 60) {
      const minHours = minMinutes / 60;
      const maxHours = maxMinutes / 60;
      
      // Format hours with appropriate precision
      const formatHours = (hours) => {
        if (hours < 1) {
          return `${Math.round(hours * 60)} minutes`;
        } else if (hours === Math.floor(hours)) {
          return `${hours} hour${hours > 1 ? 's' : ''}`;
        } else {
          return `${hours.toFixed(1)} hours`;
        }
      };
      
      return `~${formatHours(minHours)}-${formatHours(maxHours)}`;
    }
    return `~${Math.round(minMinutes)}-${Math.round(maxMinutes)} minutes`;
  }
  
  // For chains with exact timing
  if (minMinutes >= 60) {
    const hours = minMinutes / 60;
    if (hours === Math.floor(hours)) {
      return `~${hours} hour${hours > 1 ? 's' : ''}`;
    }
    return `~${hours.toFixed(1)} hours`;
  }
  return `~${Math.round(minMinutes)} minutes`;
};

// Get progress percentage based on elapsed time
export const getProgressPercentage = (startTime, chainId, mode = 'typical') => {
  if (!startTime) return 0;
  
  const elapsedMs = Date.now() - startTime;
  const elapsedMinutes = elapsedMs / (1000 * 60);
  const totalMinutes = getEstimatedFinalityTime(chainId, mode);
  
  const percentage = Math.min(95, (elapsedMinutes / totalMinutes) * 100);
  return Math.round(percentage);
};

// Get remaining time in human-readable format
export const getRemainingTime = (startTime, chainId, mode = 'typical') => {
  if (!startTime) return getFormattedFinalityTime(chainId, mode);
  
  const elapsedMs = Date.now() - startTime;
  const totalMs = getEstimatedFinalityTime(chainId, mode) * 60 * 1000;
  const remainingMs = Math.max(0, totalMs - elapsedMs);
  
  const remainingMinutes = Math.ceil(remainingMs / (1000 * 60));
  
  if (remainingMinutes === 0) {
    return 'Almost ready';
  } else if (remainingMinutes === 1) {
    return '1 minute remaining';
  } else if (remainingMinutes < 60) {
    return `${remainingMinutes} minutes remaining`;
  } else {
    const hours = Math.floor(remainingMinutes / 60);
    const mins = remainingMinutes % 60;
    if (mins === 0) {
      return `${hours} hour${hours > 1 ? 's' : ''} remaining`;
    }
    return `${hours}h ${mins}m remaining`;
  }
};